import clsx from 'clsx';

import type { SmartFillFormQuestionFieldsFragment } from '~/api/graphql';
import type { CoverageCounts, QuestionStatus } from './form-buckets';

/** Shared by the ring, the legend, the map and each question, so one status reads alike everywhere. */
export const QUESTION_STATUS: Record<
  QuestionStatus,
  { label: string; swatch: string; stroke: string }
> = {
  bucket: {
    label: 'Already in the bucket',
    swatch: 'bg-sky-500 dark:bg-sky-400',
    stroke: 'stroke-sky-500 dark:stroke-sky-400'
  },
  agent: {
    label: 'Answered by you',
    swatch: 'bg-emerald-500 dark:bg-emerald-400',
    stroke: 'stroke-emerald-500 dark:stroke-emerald-400'
  },
  needed: {
    label: 'Still needed',
    swatch: 'bg-neutral-200 dark:bg-neutral-700',
    stroke: 'stroke-neutral-200 dark:stroke-neutral-700'
  }
};

const STATUS_ORDER: QuestionStatus[] = ['bucket', 'agent', 'needed'];

const RING_RADIUS = 52;
const RING_WIDTH = 12;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const CoverageRing = ({ counts }: { counts: CoverageCounts }) => {
  const ready = counts.bucket + counts.agent;
  // Rounded down, so the ring never claims 100% while a question is still open.
  const percent = counts.total > 0 ? Math.floor((100 * ready) / counts.total) : 0;
  const arc = (count: number) =>
    counts.total > 0 ? (RING_CIRCUMFERENCE * count) / counts.total : 0;
  const segments = [
    { status: 'bucket' as const, length: arc(counts.bucket), start: 0 },
    { status: 'agent' as const, length: arc(counts.agent), start: arc(counts.bucket) }
  ];

  return (
    <div className="relative size-36 shrink-0">
      <svg viewBox="0 0 128 128" className="size-full" aria-hidden="true">
        <g transform="rotate(-90 64 64)">
          <circle
            cx="64"
            cy="64"
            r={RING_RADIUS}
            fill="none"
            strokeWidth={RING_WIDTH}
            className={QUESTION_STATUS.needed.stroke}
          />
          {segments.map((segment) => (
            <circle
              key={segment.status}
              cx="64"
              cy="64"
              r={RING_RADIUS}
              fill="none"
              strokeWidth={RING_WIDTH}
              strokeDasharray={`${segment.length} ${RING_CIRCUMFERENCE}`}
              strokeDashoffset={-segment.start}
              className={clsx(
                QUESTION_STATUS[segment.status].stroke,
                'transition-[stroke-dasharray,stroke-dashoffset] duration-700 ease-out'
              )}
            />
          ))}
        </g>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-semibold tabular-nums tracking-tight text-neutral-900 dark:text-white">
          {percent}%
        </span>
        <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">ready</span>
      </div>
    </div>
  );
};

/** The coverage ring beside the count of ready questions and what makes them up. */
export const CoverageSummary = ({ counts }: { counts: CoverageCounts }) => (
  <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
    <CoverageRing counts={counts} />
    <div className="w-full min-w-48 sm:w-auto">
      <p data-testid="coverage-headline" className="text-neutral-900 dark:text-white">
        <span className="text-3xl font-semibold tabular-nums tracking-tight">
          {counts.bucket + counts.agent}
        </span>{' '}
        <span className="text-base font-medium text-neutral-500 dark:text-neutral-400">
          of {counts.total} questions ready
        </span>
      </p>
      <dl className="mt-4 space-y-2">
        {STATUS_ORDER.map((status) => (
          <div key={status} className="flex items-center gap-2 text-sm">
            <dt className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400">
              <span
                aria-hidden="true"
                className={clsx('size-2.5 rounded-sm', QUESTION_STATUS[status].swatch)}
              />
              {QUESTION_STATUS[status].label}
            </dt>
            <dd className="ml-auto pl-6 font-medium tabular-nums text-neutral-900 dark:text-white">
              {counts[status]}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  </div>
);

/** One square per question in the order the page lists them; choosing one takes the page to it. */
export const QuestionMap = ({
  questions,
  statuses,
  caption,
  onSelect
}: {
  questions: Pick<SmartFillFormQuestionFieldsFragment, 'question'>[];
  statuses: QuestionStatus[];
  caption: string;
  onSelect: (index: number) => void;
}) => (
  <div className="min-w-0">
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">Question map</h2>
      <p className="text-xs text-neutral-500 dark:text-neutral-400">{caption}</p>
    </div>
    <ol aria-label="Question map" className="mt-3 flex flex-wrap gap-1.5">
      {questions.map((question, index) => (
        <li key={index} className="flex">
          <button
            type="button"
            title={question.question}
            aria-label={`Question ${index + 1}: ${question.question} (${QUESTION_STATUS[statuses[index]].label})`}
            onClick={() => onSelect(index)}
            className={clsx(
              'size-4 rounded-[4px] transition-transform hover:scale-125',
              'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
              QUESTION_STATUS[statuses[index]].swatch
            )}
          />
        </li>
      ))}
    </ol>
  </div>
);
